import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd'
import { Plus, X } from 'lucide-react'

// Definitions-Schritt: 7 Optionen, davon 2 priorisiert
// `priorities`: [defIdx | null, defIdx | null]
// onAssign(prioIdx, defIdx) — defIdx in [0..6], prioIdx 0|1
// onRemove(prioIdx)
export default function DefinitionPick({
  word, kind, defs, priorities, copy, onAssign, onRemove,
}) {
  const used = priorities.filter(p => p != null)
  const remaining = defs
    .map((d, i) => ({ ...d, idx: i }))
    .filter(d => !used.includes(d.idx))

  const slotForDefinition = (defIdx) => priorities.findIndex(p => p === defIdx)

  const handleClickRemaining = (defIdx) => {
    const free = priorities.findIndex(p => p == null)
    if (free === -1) return
    onAssign(free, defIdx)
  }

  const handleDragEnd = (result) => {
    if (!result.destination) return
    const { source, destination } = result

    // Source can be 'remaining' or 'slot-0' / 'slot-1'
    // Destination can be 'slot-0' or 'slot-1'
    if (source.droppableId === 'remaining' && destination.droppableId.startsWith('slot-')) {
      const slotIdx = Number(destination.droppableId.split('-')[1])
      const defIdx = remaining[source.index].idx
      onAssign(slotIdx, defIdx)
    } else if (source.droppableId.startsWith('slot-') && destination.droppableId.startsWith('slot-')) {
      const fromSlot = Number(source.droppableId.split('-')[1])
      const toSlot = Number(destination.droppableId.split('-')[1])
      if (fromSlot === toSlot) return
      const defIdx = priorities[fromSlot]
      if (defIdx == null) return
      onAssign(toSlot, defIdx)
    }
  }

  const verb = kind === 'motivators' ? copy.motivatorMotiviert : copy.demotivatorMotiviert

  return (
    <div className="space-y-6">
      <div className="text-lg">
        <span className="font-bold">{word}</span>{' '}
        <span className="text-ink-soft">{verb}</span>
      </div>

      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="space-y-3">
          {[0, 1].map(slotIdx => (
            <Slot
              key={slotIdx}
              slotIdx={slotIdx}
              defIdx={priorities[slotIdx]}
              defs={defs}
              onRemove={() => onRemove(slotIdx)}
              dropZoneLabel={copy.dropZone}
            />
          ))}
        </div>

        <div className="border-t border-teal-100 pt-5">
          <Droppable droppableId="remaining">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-2">
                {remaining.map((d, i) => (
                  <Draggable key={`r-${d.idx}`} draggableId={`r-${d.idx}`} index={i}>
                    {(prov, snap) => (
                      <button
                        ref={prov.innerRef}
                        {...prov.draggableProps}
                        {...prov.dragHandleProps}
                        type="button"
                        onClick={() => handleClickRemaining(d.idx)}
                        className={`w-full flex items-start gap-3 p-3 rounded-xl text-left transition
                          ${snap.isDragging ? 'bg-white shadow-card border-teal-300' : 'bg-teal-50/40 hover:bg-teal-100/60'}
                          border border-teal-100`}
                      >
                        <span className="w-7 h-7 rounded-full bg-teal-200 text-teal-800 grid place-items-center shrink-0 group-hover:bg-teal-300">
                          <Plus size={14} />
                        </span>
                        <span className="text-sm leading-relaxed pt-0.5">{d.text}</span>
                      </button>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </div>
      </DragDropContext>
    </div>
  )
}

function Slot({ slotIdx, defIdx, defs, onRemove, dropZoneLabel }) {
  const def = defIdx != null ? defs[defIdx] : null
  return (
    <Droppable droppableId={`slot-${slotIdx}`}>
      {(provided, snap) => (
        <div
          ref={provided.innerRef}
          {...provided.droppableProps}
          className={`rounded-xl p-3 min-h-[68px] flex items-start gap-3 transition border-2
            ${snap.isDraggingOver ? 'border-teal bg-teal-50' : def ? 'border-teal-200 bg-teal-50/30' : 'border-dashed border-teal-200 bg-white'}
          `}
        >
          <span className={`badge h-7 w-24 justify-center shrink-0
            ${def ? 'bg-teal text-white' : 'bg-teal-100 text-teal-800'}`}>
            Priorität {slotIdx + 1}
          </span>
          {def ? (
            <Draggable draggableId={`s-${slotIdx}`} index={0}>
              {(prov) => (
                <div
                  ref={prov.innerRef}
                  {...prov.draggableProps}
                  {...prov.dragHandleProps}
                  className="flex-1 text-sm leading-relaxed pt-0.5 cursor-grab"
                >
                  {def.text}
                </div>
              )}
            </Draggable>
          ) : (
            <span className="flex-1 text-sm text-ink-mute italic pt-0.5">{dropZoneLabel}</span>
          )}
          {def && (
            <button onClick={onRemove} className="text-ink-mute hover:text-rose-500 p-1" aria-label="Auswahl entfernen">
              <X size={16} />
            </button>
          )}
          {provided.placeholder}
        </div>
      )}
    </Droppable>
  )
}
